// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, BaseSelect, BaseUploadImage, Loading, UnderDevelopment } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { updateInventory } from '~/thunks/inventory/inventoryThunk';
import { inventoryActions } from '~/thunks/inventory/inventorySlice';
import { IFormInventory, IItemVariant } from '~/utils/interface/inventory';
import { EMPTY_STRING, optionColor, REG_EXP } from '~/utils/constants/common';
import { defaultValuesVariantForm, schemaVariant } from '~/pages/common/inventories/inventory/inventoryForm/helper';
import { IBaseFile, IBaseOption, IBodyUpdate, IResultCallApiFn } from '~/utils/interface/common';
import { compressedImage, createFormData, formatInputCurrency, generateUniqueId } from '~/utils/helper/common';
// Styles, images, icons
import styles from './ProductVariantFormModal.module.scss';
import { icons } from '~/assets';

type Props = {
  dataVariant?: IItemVariant | null;
  indexItem?: number;
  isDetail?: boolean;
  onClose: () => void;
  onRemoveFile?: (id: string) => void;
};

const cx = classNames.bind(styles);

const ProductVariantFormModal = (props: Props) => {
  //#region Destructuring Props
  const { dataVariant, indexItem, isDetail, onClose, onRemoveFile } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: inventoryId } = useParams();

  const { control: controlInventory } = useFormContext<IFormInventory>();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IItemVariant>({
    resolver: yupResolver(schemaVariant(t)),
    defaultValues: defaultValuesVariantForm,
  });

  const { append: appendItem, update: updateItem } = useFieldArray({
    control: controlInventory,
    name: 'variants',
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [removeFileId, setRemoveFileId] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!dataVariant) return;

    reset(dataVariant);
  }, [dataVariant]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleFormVariant = (data: IItemVariant) => {
    if (dataVariant) {
      handleEditVariant(data);
    } else {
      handleAddVariant(data);
    }
  };

  const handleAddVariant = (data: IItemVariant) => {
    appendItem({ ...data, id: generateUniqueId(), uuid: generateUniqueId() });
    handleCloseModal();
  };

  const handleEditVariant = (data: IItemVariant) => {
    if (!dataVariant) return;

    if (isDetail) {
      handleIntegrateEditVariant(data);
    } else {
      updateItem(Number(indexItem), { ...data, uuid: generateUniqueId() });
      handleCloseModal();
    }
  };

  const handleIntegrateEditVariant = async (data: IItemVariant) => {
    if (!data) return;

    const { variantImage, ...rest } = data;

    const dataUpdate = {
      variants: [
        {
          ...rest,
          size: Number(rest.size),
          height: Number(rest.height),
          weight: Number(rest.weight),
          length: Number(rest.length),
          uuid: generateUniqueId(),
        },
      ],
      ...(removeFileId && { deleteFiles: [removeFileId] }),
    };

    const formData = await createFormData(dataUpdate);

    if (variantImage?.file) {
      const image = await compressedImage(variantImage?.file);
      formData.append(`variantImage_${dataUpdate?.variants?.[0]?.uuid}`, image);
    }

    const params: IBodyUpdate<FormData> = {
      id: inventoryId || EMPTY_STRING,
      body: formData,
    };

    setIsLoading(true);
    setRemoveFileId(EMPTY_STRING);

    dispatch(updateInventory(params))
      .unwrap()
      .then((_res) => {
        handleCloseModal();
        dispatch(inventoryActions.setRefreshInventoryDetail(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRemoveFile = (file: IBaseFile) => {
    if (!file.id) return;

    setRemoveFileId(file.id);
    onRemoveFile?.(file.id);
  };

  const fetchOptionsColor = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionColor,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Dialog id='productVariantFormModalComponent' open={true} onClose={handleCloseModal} fullWidth maxWidth={'md'}>
      <DialogTitle className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(dataVariant ? 'inventory_form_product_variant_edit' : 'inventory_form_product_variant_add')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={12}>
            <Controller
              control={control}
              name={'variantImage'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseUploadImage
                    height={130}
                    label={t('inventory_form_product_variant_photo')}
                    isButton={false}
                    isMultiple={false}
                    uploadedFiles={[value || {}]}
                    onChange={(files: IBaseFile[]) => onChange(files?.[0])}
                    errorMessage={errors.variantImage?.message}
                    onRemove={handleRemoveFile}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_name')}
                    placeholder={t('inventory_form_product_variant_enter_name')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'style'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_style')}
                    placeholder={t('inventory_form_product_variant_enter_style')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.style?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'color'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('inventory_form_product_variant_color')}
                    placeholder={t('inventory_form_product_variant_select_color')}
                    callApiFn={fetchOptionsColor}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.color?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'length'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_length')}
                    placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                    value={String(value)}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event.target.value));
                    }}
                    errorMessage={errors?.length?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'size'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_size')}
                    placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                    value={String(value)}
                    onChange={(e) => {
                      const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                      const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                      onChange(String(numericValue));
                    }}
                    errorMessage={errors?.size?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'height'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_height')}
                    placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                    value={String(value)}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event.target.value));
                    }}
                    errorMessage={errors?.height?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'weight'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_weight')}
                    placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                    value={String(value)}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event.target.value));
                    }}
                    errorMessage={errors?.weight?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'quantity'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_quantity')}
                    placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                    value={String(value)}
                    onChange={(e) => {
                      const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                      const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                      onChange(String(numericValue));
                    }}
                    errorMessage={errors?.quantity?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'rentalPrice'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_product_variant_rental_price')}
                    placeholder={t('common_text_placeholder_money', { ns: 'common/base' })}
                    value={value}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event.target.value));
                    }}
                    errorMessage={errors?.rentalPrice?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'description'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    multiline
                    rows={7}
                    height={'auto'}
                    label={t('inventory_form_product_variant_description')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.description?.message}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          disabled={dataVariant ? !isDirty : false}
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormVariant)}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Dialog>
  );
};

export default ProductVariantFormModal;
